import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import companyLogo from './../assets/images/logo-no-bg.png';
import AuthService from '../services/AuthService';
import styles from './../styles/AuthPages.module.css';

const cx = (...names) => names.map((name) => styles[name]).join(' ');

function ForgotPasswordPage() {
  const [workEmail, setWorkEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const canSubmit = useMemo(() => {
    return workEmail.trim().length > 0;
  }, [workEmail]);

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!canSubmit || isSubmitting) {
      return;
    }

    const submit = async () => {
      setError('');
      setSuccessMessage('');
      setIsSubmitting(true);

      try {
        const response = await AuthService.forgotPassword({
          email: workEmail.trim(),
        });

        if (!response?.isSuccess) {
          setError(response?.message || 'Unable to submit password reset request.');
          return;
        }

        setSubmitted(true);
        setSuccessMessage(
          response?.message || 'If the account exists, reset instructions will be emailed shortly.'
        );
      } catch (submitError) {
        setError(submitError?.message || 'Unable to submit password reset request.');
      } finally {
        setIsSubmitting(false);
      }
    };

    submit();
  };

  return (
    <div className={styles['forgot-password-page']}>
      <section className={styles['forgot-password-shell']}>
        <div className={cx('forgot-password-panel', 'forgot-password-brand')}>
          <div className={styles['forgot-password-brand-top']}>
            <img src={companyLogo} alt="Company Logo" className={styles['forgot-password-logo']} />
          </div>

          <div className={styles['forgot-password-brand-copy']}>
            <h2>Forgot your password?</h2>
            <p>
              Enter your account email to receive a secure password reset link and regain access to
              your account.
            </p>
          </div>

          <div className={styles['forgot-password-brand-notes']}>
            <div>
              <strong>Fastest route</strong>
              <span>Use the same email address linked to your account profile.</span>
            </div>
            <div>
              <strong>Security first</strong>
              <span>Do not share old passwords or one-time codes in this request.</span>
            </div>
          </div>
        </div>

        <form className={cx('forgot-password-panel', 'forgot-password-card')} onSubmit={handleSubmit}>
          <div className={styles['forgot-password-header']}>
            <div>
              <h2>Request a password reset</h2>
              <p>We’ll send a reset link if the details match an active account.</p>
            </div>
            <Link to="/login" className={styles['forgot-password-link']}>
              Back to login
            </Link>
          </div>

          {submitted ? (
            <div className={styles['forgot-password-success']}>
              <h3>Request captured</h3>
              <p>
                {successMessage ||
                  'If the details match an account, reset instructions will be emailed shortly.'}
              </p>
              <p className={styles['forgot-password-support-note']}>
                If you do not receive the reset email after 5 minutes, please contact support.
              </p>
              <Link to="/login" className={styles['forgot-password-primary']}>
                Return to login
              </Link>
            </div>
          ) : (
            <>
              {error && <div className={cx('forgot-password-alert', 'error')}>{error}</div>}

              <label className={styles['forgot-password-field']}>
                <span>Email address</span>
                <input
                  type="email"
                  value={workEmail}
                  onChange={(event) => setWorkEmail(event.target.value)}
                  placeholder="name@company.com"
                  autoComplete="email"
                  disabled={isSubmitting}
                />
              </label>

              <div className={styles['forgot-password-help']}>
                <div className={styles['forgot-password-help-item']}>
                  <strong>What happens next</strong>
                  <span>
                    If the details are valid, an email will be sent with a secure link to set a new
                    password.
                  </span>
                </div>
                <div className={styles['forgot-password-help-item']}>
                  <strong>Check your inbox</strong>
                  <span>Also look in spam or junk folders if the email does not appear quickly.</span>
                </div>
                <div className={cx('forgot-password-help-item', 'forgot-password-support-note')}>
                  <strong>Need help?</strong>
                  <span>
                    If you do not receive the reset email after 5 minutes, please contact support.
                  </span>
                </div>
              </div>

              <div className={styles['forgot-password-actions']}>
                <Link to="/login" className={styles['forgot-password-secondary']}>
                  Cancel
                </Link>
                <button
                  type="submit"
                  className={styles['forgot-password-primary']}
                  disabled={!canSubmit || isSubmitting}
                >
                  {isSubmitting ? 'Submitting...' : 'Recover password'}
                </button>
              </div>
            </>
          )}
        </form>
      </section>
    </div>
  );
}

export default ForgotPasswordPage;
